/**
 * Running configuration service with caching
 * Handles fetching and caching user's running parameters
 * Falls back to user parameters (rider_weight) for body weight if not set
 */

import { getRunningConfigurations } from '../api/runningApi';
import { getUserParameters } from './userParametersService';

// Cache configuration
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
let cachedConfig = null;
let cacheTimestamp = null;

/**
 * Default running configuration for non-authenticated users
 */
export const DEFAULT_RUNNING_CONFIG = {
  body_weight: 70.0,
  threshold_pace: 5.0,
  threshold_heart_rate: null,
  max_heart_rate: 185,
  resting_heart_rate: null
};

/**
 * Get running configuration with caching
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @returns {Promise<Object>} Running configuration object
 */
export const getRunningConfig = async (isAuthenticated) => {
  // Return default config for non-authenticated users
  if (!isAuthenticated) {
    return DEFAULT_RUNNING_CONFIG;
  }

  // Check if cache is still valid
  const now = Date.now();
  if (cachedConfig && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION)) {
    return cachedConfig;
  }

  // Start with user's rider weight as baseline for body weight
  let riderWeight = DEFAULT_RUNNING_CONFIG.body_weight;
  try {
    const userParams = await getUserParameters(isAuthenticated);
    console.log('[RunningConfig] User parameters response:', userParams);
    if (userParams && userParams.rider_weight) {
      riderWeight = userParams.rider_weight;
      console.log('[RunningConfig] Using rider weight:', riderWeight);
    }
  } catch (error) {
    console.log('[RunningConfig] Could not load user parameters for weight:', error);
  }

  // Fetch running configuration
  try {
    const configs = await getRunningConfigurations();
    console.log('[RunningConfig] Running configs response:', configs);
    if (configs && configs.length > 0) {
      cachedConfig = configs[0];
      console.log('[RunningConfig] Running config body_weight:', cachedConfig.body_weight);

      // Use rider weight if running config has default weight (70.0)
      // This ensures weight is always synced with user parameters unless explicitly changed
      const currentWeight = parseFloat(cachedConfig.body_weight);
      console.log('[RunningConfig] Parsed weight:', currentWeight, 'Rider weight:', riderWeight);
      if (!currentWeight || currentWeight === 70.0) {
        console.log('[RunningConfig] Using rider weight as fallback');
        cachedConfig.body_weight = riderWeight;
      }

      cacheTimestamp = now;
      console.log('[RunningConfig] Final config:', cachedConfig);
      return cachedConfig;
    }
  } catch (error) {
    console.error('[RunningConfig] Failed to load running configuration:', error);
  }

  // No running config exists - return default with rider weight
  console.log('[RunningConfig] No running config found, using default with rider weight:', riderWeight);
  const defaultWithRiderWeight = { ...DEFAULT_RUNNING_CONFIG, body_weight: riderWeight };
  cachedConfig = defaultWithRiderWeight;
  cacheTimestamp = now;
  return defaultWithRiderWeight;
};

/**
 * Clear the cached configuration
 * Call this after updating configuration to force a fresh fetch
 */
export const clearRunningConfigCache = () => {
  cachedConfig = null;
  cacheTimestamp = null;
};

/**
 * Set the cached configuration manually
 * Useful after creating or updating a configuration
 * @param {Object} config - Configuration object to cache
 */
export const setCachedRunningConfig = (config) => {
  cachedConfig = config;
  cacheTimestamp = Date.now();
};
